import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, Leaf, Waves, MapPin, ArrowUpRight } from "lucide-react";
import Reveal from "../components/site/Reveal";
import { CtaLink } from "../components/site/Common";
import { OrganicDot } from "../components/site/Logo";
import { FinalCta } from "../components/site/HomeSections";
import { OPTION_PAGES, OPTION_CITIES, optionPath, optionCityPath, optionCity } from "../options";

const ICONS = { Leaf, Waves };

function OptionFaq({ faq }) {
  return (
    <section className="pb-20" data-testid="option-faq">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <h2 className="font-logo font-black tracking-[-0.025em] text-3xl lg:text-4xl text-[#13261c]">Questions fréquentes</h2>
        <div className="mt-8 space-y-4">
          {faq.map(([q, a], i) => (
            <Reveal key={q} delay={i * 80} className="rounded-3xl bg-white ring-1 ring-[#e6dcc8] p-6" data-testid={`option-faq-${i}`}>
              <h3 className="font-bold text-[#13261c] text-lg">{q}</h3>
              <p className="mt-2 text-[#5a6158] leading-relaxed">{a}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function OptionText({ blocks }) {
  return (
    <section className="py-16" data-testid="option-text">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-6">
        {blocks.map(([h, t], i) => (
          <Reveal key={h} delay={i * 80} className="rounded-[28px] bg-[#f3ede1] p-7">
            <h2 className="font-logo font-black text-xl text-[#13261c] leading-snug">{h}</h2>
            <p className="mt-3 text-[#5a6158] leading-relaxed">{t}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function OptionCities({ o, current }) {
  return (
    <section className="pb-20" data-testid="option-cities">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <h2 className="font-logo font-black tracking-[-0.025em] text-2xl lg:text-3xl text-[#13261c]">{o.name} dans votre commune</h2>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {OPTION_CITIES.filter((c) => c !== current).map((c) => (
            <Link key={c} to={optionCityPath(o, c)} data-testid={`option-city-${optionCityPath(o, c).split("/").pop()}`} className="group flex items-center justify-between rounded-2xl p-4 ring-1 ring-[#e6dcc8] bg-white hover:ring-[#d9895a] transition-colors">
              <span className="flex items-center gap-2 font-bold text-sm text-[#13261c]"><MapPin className="w-4 h-4 text-[#c4703f]" />{o.name} {c}</span>
              <ArrowUpRight className="w-4 h-4 opacity-40 group-hover:opacity-100" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function OptionPage() {
  const { slug, city } = useParams();
  const o = OPTION_PAGES.find((x) => x.slug === slug);
  const cityName = o && city ? OPTION_CITIES.find((c) => optionCityPath(o, c) === `/options/${slug}/${city}`) : null;
  if (!o || (city && !cityName)) return <Navigate to="/tarifs" replace />;
  const r = cityName ? optionCity(o, cityName) : null;
  const I = ICONS[o.icon];
  return (
    <div data-testid="option-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#d9895a]/15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-16 pb-16 grid lg:grid-cols-12 gap-10 items-start relative">
          <Reveal className="lg:col-span-7">
            <nav className="text-sm font-semibold text-[#6d7268]"><Link to="/" className="hover:text-[#13261c]">Accueil</Link> <span className="mx-1.5">/</span><Link to="/tarifs" className="hover:text-[#13261c]">Tarifs</Link> <span className="mx-1.5">/</span>{r ? <><Link to={optionPath(o)} className="hover:text-[#13261c]">{o.name}</Link> <span className="mx-1.5">/</span><span className="text-[#13261c]">{cityName}</span></> : <span className="text-[#13261c]">{o.name}</span>}</nav>
            <h1 className="mt-6 text-[#13261c]" data-testid="option-title">
              <span className="block font-logo font-black tracking-[-0.03em] text-[40px] sm:text-[56px] lg:text-[64px] leading-[1]">{o.name}<OrganicDot className="inline-block align-baseline ml-1 w-3 h-3" /></span>
              <span className="block font-script text-[28px] sm:text-[38px] text-[#d9895a] leading-[1.2] mt-2">{r ? `location saisonnière ${r.at}` : o.script}</span>
            </h1>
            <p className="mt-5 text-lg text-[#4a5249] leading-relaxed max-w-2xl">{r ? r.intro : o.intro}</p>
            <p className="mt-6 inline-flex items-center gap-3 rounded-full bg-white ring-1 ring-[#e6dcc8] px-5 py-3" data-testid="option-price">
              <span className="font-logo font-black text-xl text-[#c4703f]">{o.price}</span><span className="text-sm text-[#6d7268]">en supplément de la formule</span>
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/rendez-vous" testid="option-rdv">Demander un devis</CtaLink>
              <CtaLink to="/tarifs" variant="outline" testid="option-tarifs">Voir les formules</CtaLink>
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5 rounded-[32px] bg-[#13261c] text-[#faf7f0] p-8 grain relative overflow-hidden" data-testid="option-included">
            <span className="w-12 h-12 rounded-2xl bg-[#d9895a] grid place-items-center"><I className="w-6 h-6 text-[#13261c]" /></span>
            <p className="mt-6 font-bold text-[#d9895a] uppercase tracking-[0.14em] text-sm">Ce qui est inclus</p>
            <ul className="mt-4 space-y-3">{o.included.map((t) => <li key={t} className="flex gap-3"><Check className="w-4 h-4 mt-1 shrink-0 text-[#d9895a]" />{t}</li>)}</ul>
            <p className="mt-5 text-sm text-[#faf7f0]/70">Option disponible avec les formules Essentiel, Sérénité et Premium.</p>
          </Reveal>
        </div>
      </section>
      {o.photo && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-4" data-testid="option-photo">
          <Reveal as="figure" className="relative rounded-[32px] overflow-hidden shadow-[0_40px_80px_-40px_rgba(19,38,28,0.5)]">
            <img src={o.photo} alt={r ? `${o.photoAlt} ${r.at}` : o.photoAlt} loading="lazy" className="w-full h-[260px] sm:h-[380px] lg:h-[480px] object-cover" />
            <figcaption className="absolute left-5 bottom-5 rounded-full bg-[#13261c]/85 backdrop-blur-md text-[#faf7f0] text-sm font-semibold px-4 py-2">{o.photoCaption}</figcaption>
          </Reveal>
        </section>
      )}
      <OptionText blocks={r ? [[`${o.name} ${r.at}`, r.paras[0]], ["Des passages calés sur vos séjours", r.paras[1]], ["Prix et devis", r.paras[2]]] : o.sections} />
      <OptionFaq faq={r ? r.faq : o.faq} />
      <OptionCities o={o} current={cityName} />
      <FinalCta />
    </div>
  );
}
